import CimWorkloadModule from '@/models/CimWorkloadModule'
import { RegionOptions } from '@/models/interface/Common'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import { buildColorPool } from '@/models/Util'
import { clone, startCase } from 'lodash'
import moment from 'moment'
import numeral from 'numeral'
import DateRangeFilter from '../../filters/DateRangeFilter'
import { FILTERS } from '../../helpers'

export default class NumberOfTasksWidget extends SystemDashboardWidget {
  public title: string = 'Number of Tasks'

  public key: string = 'number-of-tasks'

  public view: string = 'chart'

  public subView: string = 'line'

  public dimensions: string[] = ['date', 'name']

  public metrics: string[] = ['count']

  public loaderSize: string = '350px'

  public size: any = {
    lg: 6,
    md: 6,
    sm: 12,
  }

  public dataSourceRequest: any = null

  public format_totals: any = '0.00a'

  public cumulative: boolean = false

  public date_dimension_format: string = 'YYYY-MM-DD'

  public noSort: boolean = false

  constructor(args: any = null) {
    super()

    if (args && args.dimensions) {
      this.dimensions = args.dimensions
    }

    if (args && args.metrics) {
      this.metrics = args.metrics
    }

    if (args && args.filters) {
      this.filters = Object.assign(this.filters, args.filters)
    }
    if (args && args.internalFilters) {
      this.internalFilters = Object.assign(this.internalFilters, args.internalFilters)
    }
    if (args && args.internalQuery) {
      this.internalQuery = Object.assign(this.internalQuery, args.internalQuery)
    }

    if (args && args.rightCol) {
      this.rightCol = args.rightCol
    }

    if (args && args.leftCol) {
      this.leftCol = args.leftCol
    }

    if (args && args.size) {
      this.size = args.size
    }

    if (args && args.title) {
      this.title = args.title
    }

    if (args && args.key) {
      this.key = args.key
    }

    if (args && args.dataSourceRequest) {
      this.dataSourceRequest = args.dataSourceRequest
    }

    // format_totals
    if (args && args.format_totals) {
      this.format_totals = args.format_totals
    }

    // cumulative
    if (args && args.hasOwnProperty('cumulative')) {
      this.cumulative = args.cumulative
    }

    if (args && args.internalGroups) {
      this.internalGroups = args.internalGroups
    }

    if (args && args.groups) {
      this.groups = args.groups
    }
  }

  // API Filters
  public filters: any = {
    region: 'all',
    product: 'all',
  }

  public groups: any = {}

  public internalFilters: any = {
    ...FILTERS.YEAR,
  }

  public rightCol: any = [new DateRangeFilter({ target: 'internalFilters' })]

  public get filterToQuery() {
    let query = []

    if (this.filters.product !== 'all') {
      query.push(`product:${this.filters.product}`)
    }

    if (this.filters.region !== 'all') {
      query.push(`region:${this.filters.region}`)
    }

    return query
  }

  public get groupList() {
    let group_mode = this.groups.period
    let group_role = this.groups.view

    if (group_role === 'sales_rep') {
      group_role = 'individual'
    } else if (group_role === 'region') {
      group_role = 'team'
    }

    return {
      group_mode,
      group_role,
    }
  }

  public get payload() {
    const query = [...new Set([...this.query, ...this.internalQuery, ...this.filterToQuery])]

    let payload: any = {
      query,
      start: this.internalFilters.start,
      end: this.internalFilters.end,
      shortcut: this.internalFilters.shortcut,
      dimensions: this.dimensions,
      metrics: this.metrics,
      ...this.groupList,
    }

    if (this.internalFilters.ignore_dimension) {
      delete payload.dimensions
    }
    if (this.internalFilters.ignore_metrics) {
      delete payload.metrics
    }
    return payload
  }

  public formatters: any = {
    yaxis: (_: SystemDashboardWidget, value: any) => `${numeral(value).format(this.format_totals)}`,
    label: (_: SystemDashboardWidget, value: any) => `${numeral(value).format(this.format_totals)}`,
    tooltip: (_: SystemDashboardWidget, value: any) => [numeral(value).format(this.format_totals)],
  }

  public dataSource() {
    if (this.dataSourceRequest) {
      const res = this.dataSourceRequest(this.payload)

      return res
    }
    let request = new CimWorkloadModule()

    return request.numberOfTaskRequest(this.payload).then((res: any) => res.result)
  }

  protected dataMapper(data: any): Promise<any> {
    const series: number[] = []
    const data_series: any[] = []
    const labels: any[] = []
    const colors: string[] = []
    const series_ids: string[] = []
    let total = 0
    let total_cumulative: any[] = []

    const groups: string[] = []

    let x_series: string[] = []

    // Sort if applicable
    if (this.date_dimension_format && !this.noSort) {
      data = data.sort(
        (a: any, b: any) =>
          moment(a[this.dimensions[0]], this.date_dimension_format).unix()
          - moment(b[this.dimensions[0]], this.date_dimension_format).unix(),
      )
    }

    // Init Series
    data.forEach((item: any, idx: number) => {
      const label = item[this.dimensions[1]]
      const group_index = groups.indexOf(label)
      if (group_index === -1) {
        let name = label
        if (!label.includes('N/A')) {
          name = startCase(label)
        }
        groups.push(label)
        data_series.push({ name, data: [] })
      }

      const x_label = item[this.dimensions[0]]

      if (x_series.indexOf(x_label) === -1) {
        x_series.push(x_label)
      }
    })

    const color_pool = buildColorPool(groups.length)

    data_series.forEach((item: any, idx: number) => {
      x_series.forEach(() => {
        item.data.push(0)
      })
      if (item.name === 'CCL') {
        colors.push('#555c6e')
      } else if (item.name === 'SSL') {
        colors.push('#00C1DE')
      } else {
        colors.push(color_pool(idx))
      }
    })

    data.forEach((item: any, idx: number) => {
      const label = item[this.dimensions[0]]
      const group_label = item[this.dimensions[1]]
      const group_index = groups.indexOf(group_label)
      const x_index = x_series.indexOf(label)

      let val = Number(item[this.metrics[0]])
      if (isNaN(val)) {
        val = 0
      }
      if (!total_cumulative[group_index]) {
        total_cumulative[group_index] = 0
      }

      total += val
      total_cumulative[group_index] += val

      if (this.cumulative) {
        data_series[group_index].data[x_index] = clone(total_cumulative[group_index])
        series.push(total_cumulative[group_index])
      } else {
        series.push(val)

        data_series[group_index].data[x_index] += val
      }

      if (['product', 'region', 'deal_type'].includes(this.dimensions[1])) {
        series_ids[group_index] = item[this.dimensions[1]].toLowerCase().replaceAll(' ', '_')
      } else if (this.dimensions[2]) {
        series_ids[group_index] = item[this.dimensions[2]]
      }
    })

    // Udpate Settings
    this.chartSettings.colors = colors
    this.chartSettings.xaxis = {
      categories: x_series,
    }

    this.chartSettings.labels = labels

    return Promise.resolve(data_series)
  }
}
